import { useEffect, useMemo } from 'react'
import { Controller, useFormContext } from 'react-hook-form'
import { Checkbox, FormControlLabel, Stack } from '@mui/material'
import { CepSearchField } from '@/components/address-form/cep-search-field'
import { AutocompleteInput, TextInput } from '@/components/inputs'
import { useReadMunicipalities } from '@/api/accounting-dealer/person'
import { states } from '@/constants/states'
import { CepResponse } from '@/types'
import { RcpQuoteFormData } from './schema'

type AddressPrefix = 'residential_address' | 'billing_address'

type RcpAddressFieldsProps = {
  prefix: AddressPrefix
}

export function RcpAddressFields({ prefix }: RcpAddressFieldsProps) {
  const { control, setValue, watch, clearErrors, formState } =
    useFormContext<RcpQuoteFormData>()

  const stateCode = watch(`${prefix}.state_code`)
  const ibgeCode = watch(`${prefix}.ibge_code`)
  const noNumber = watch(`${prefix}.no_number`)

  const { data: municipalities, isLoading: readingMunicipalities } =
    useReadMunicipalities(stateCode)

  const municipalityOptions = useMemo(
    () => municipalities?.map((municipality) => municipality.ibge_code) || [],
    [municipalities],
  )

  const handleFindCep = (cep: CepResponse) => {
    setValue(`${prefix}.street`, cep.street)
    setValue(`${prefix}.ibge_code`, cep.ibge_code)
    setValue(`${prefix}.state_code`, cep.state_code)
    setValue(`${prefix}.district`, cep.district)
    clearErrors(prefix)
  }

  useEffect(() => {
    if (!ibgeCode || !municipalities?.length) return

    const municipality = municipalities.find(
      (item) => item.ibge_code === ibgeCode,
    )
    if (municipality) {
      setValue(`${prefix}.city`, municipality.name, { shouldValidate: true })
    }
  }, [ibgeCode, municipalities, prefix, setValue])

  useEffect(() => {
    if (noNumber) {
      setValue(`${prefix}.number`, 'S/N')
      clearErrors(`${prefix}.number`)
    }
  }, [clearErrors, noNumber, prefix, setValue])

  return (
    <Stack spacing={2}>
      <Controller
        name={`${prefix}.zip_code`}
        control={control}
        render={({ field, fieldState: { error } }) => (
          <CepSearchField
            label="CEP"
            sx={{ width: { xs: '100%', md: 200 } }}
            error={Boolean(error)}
            helperText={error?.message}
            onFindCep={handleFindCep}
            {...field}
          />
        )}
      />

      <Stack direction={{ xs: 'column', md: 'row' }} gap={2}>
        <TextInput
          control={control}
          name={`${prefix}.street`}
          label="Logradouro"
        />

        <TextInput
          control={control}
          name={`${prefix}.number`}
          label="Número"
          disabled={noNumber}
          sx={{ width: { xs: '100%', md: 140 } }}
        />
      </Stack>

      <Controller
        name={`${prefix}.no_number`}
        control={control}
        render={({ field }) => (
          <FormControlLabel
            control={
              <Checkbox
                checked={field.value}
                onChange={(event) => field.onChange(event.target.checked)}
              />
            }
            label="Sem número"
          />
        )}
      />

      <Stack direction={{ xs: 'column', md: 'row' }} gap={2}>
        <TextInput
          control={control}
          name={`${prefix}.complement`}
          label="Complemento"
        />
        <TextInput
          control={control}
          name={`${prefix}.district`}
          label="Bairro"
        />
      </Stack>

      <Stack direction={{ xs: 'column', md: 'row' }} gap={2}>
        <AutocompleteInput
          control={control}
          name={`${prefix}.state_code`}
          label="Estado"
          options={states.map((state) => state.code)}
        />

        <AutocompleteInput
          control={control}
          name={`${prefix}.ibge_code`}
          label="Cidade"
          options={municipalityOptions}
          loading={readingMunicipalities}
          getOptionLabel={(option) =>
            municipalities?.find((item) => item.ibge_code === option)?.name ??
            ''
          }
          textFieldProps={{
            error: Boolean(formState.errors[prefix]?.city),
            helperText: formState.errors[prefix]?.city?.message,
          }}
        />
      </Stack>
    </Stack>
  )
}
